/**
 * THE DEEP PIVOT TABLE, as a CSV for statistical work.
 *
 * <h2>The column set is declared ONCE and the table renders from it too</h2>
 *
 * The export exists so the scan can be correlated in a spreadsheet, which means the CSV is the
 * product and the on-screen table is the preview. Two column lists would be two chances to add a
 * field to one and not the other — and the direction that fails silently is the one where the
 * analyst's file is missing the column they are trying to correlate on.
 */

/**
 * Every column, in order, as `[header, accessor]`.
 *
 * <p>BOTH tiering quantities are exported, not just the one that produced the tag. Conditions A
 * and B band on the DIFFERENCE between the two orbs; condition C bands on the LARGER of them, per
 * the specification's wording — so a C tier is not comparable with an A. Shipping both means the
 * whole table can be re-tiered in the spreadsheet without re-running the scan, which is the point
 * of exporting it at all.
 */
export const DEEP_PIVOT_COLUMNS = [
  ['Date', (r) => r.date],
  ['Price', (r) => num(r.price, 4)],
  ['Pivot Type', (r) => r.pivotType],
  ['Window N', (r) => r.windowN],
  ['Leg Duration (bars)', (r) => r.legBars],
  ['Leg Abs', (r) => num(r.legAbsolute, 4)],
  ['Leg % Change', (r) => num(r.legPercent, 2)],
  ['Structure', (r) => r.structure],
  ['Category', (r) => r.category],
  ['Condition', (r) => r.condition],
  ['Shared Planet', (r) => r.sharedPlanet],
  ['Aspect 1', (r) => r.aspect1],
  ['Aspect 2', (r) => r.aspect2],
  ['Orb Difference', (r) => num(r.orbDifference, 4)],
  ['Max Orb', (r) => num(r.maxOrb, 4)],
  ['Aspect 1 Orb', (r) => num(r.aspect1Orb, 4)],
  ['Aspect 2 Orb', (r) => num(r.aspect2Orb, 4)],
  ['Aspect 1 Allowed Orb', (r) => num(r.aspect1AllowedOrb, 4)],
  ['Aspect 2 Allowed Orb', (r) => num(r.aspect2AllowedOrb, 4)],
];

/**
 * A number for a spreadsheet, or an EMPTY cell.
 *
 * <p>Empty rather than a zero or a dash. A pivot that matched no condition has no orb difference,
 * and writing 0 there would be a measurement nobody took — it would sort to the front of a
 * column, average into every statistic, and look like the tightest row in the file.
 */
const num = (value, places) =>
  (Number.isFinite(value) ? value.toFixed(places) : '');

/**
 * RFC-4180 quoting: wrap in quotes, double any quote inside.
 *
 * <p>Every field is quoted rather than only the ones that need it. An aspect reads
 * `Moon–Saturn Trine (orb 1.23 of 6.54)` and the en-dash, the parentheses and the spaces are all
 * harmless — but the day somebody adds a comma to that format, an unquoted writer silently shifts
 * every column after it by one, which is a corruption no reader of the file would notice.
 */
const cell = (value) => `"${String(value === null || value === undefined ? '' : value)
  .replace(/"/g, '""')}"`;

/** The table as CSV text, header row first. */
export const deepPivotCsv = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [DEEP_PIVOT_COLUMNS.map(([header]) => cell(header)).join(',')];
  for (const row of list) {
    lines.push(DEEP_PIVOT_COLUMNS.map(([, read]) => cell(read(row))).join(','));
  }
  // A TRAILING NEWLINE. POSIX tools treat a file whose last line has none as truncated, and some
  // readers drop that row outright -- which would quietly lose the oldest or newest pivot.
  return `${lines.join('\n')}\n`;
};

/** `SPY-deep-pivot-2026-10-07.csv` — the symbol and the day, so two downloads never collide. */
export const deepPivotFilename = (symbol, isoDate) =>
  `${String(symbol || 'symbol').toUpperCase()}-deep-pivot-${isoDate}.csv`;
